import { useEffect, useRef, useState, useCallback, useContext } from "react";
import styles from "./meetingRecorder.module.css";
import { useLocation, useNavigate, UNSAFE_NavigationContext } from "react-router-dom";
import { useStartProcessingMutation } from "../../services/api/auth.api";
import { useDismissRecordingSessionMutation, useLazyGetPendingRecordingSessionsQuery, useFinalizeRecoveredSessionMutation } from "../../services/api/user.api";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import BackArrowButton from "../backArrowButton/backArrowButton";
import Modal2 from "../modals/modal/Modal";
import DeleteConfirmation from "../modals/deleteConfirmation/deleteConfirmation";
import { StreamingUploader, getIncompleteSessions, ConnectionQuality, isStreamingAvailable } from "../../services/streamingUploader";
import { loadAppConfig, resolveApiBaseUrl } from "../../utils/config";
import { useActions } from "../../services/hooks/useActions";
import { useAppSelector } from "../../services/hooks";
import { therapistIdSelector } from "../../services/selectors/user.selectors";
import { setActiveRecording, clearActiveRecording, getActiveRecording, touchRecordingHeartbeat, pauseRecordingHeartbeat, HEARTBEAT_INTERVAL_MS } from "../../utils/logoutLock";
import { getSupportedRecordingMimeType } from "../../utils/recording";

type RecorderState = "idle" | "recording" | "paused" | "finished" | "processing";

// Screen Wake Lock API types (not yet in all TypeScript DOM libs)
interface WakeLockSentinel extends EventTarget {
  readonly released: boolean;
  readonly type: 'screen';
  release(): Promise<void>;
}

interface WakeLock {
  request(type: 'screen'): Promise<WakeLockSentinel>;
}

interface ProcessingStage {
  end: number;
  titleKey: string;
  textKey: string;
}

// Audio level monitoring constants
const SILENCE_THRESHOLD = 0.015; // RMS level below which we consider "silence"
const SILENCE_BANNER_MS  = 30_000;  // 30s  → show visual banner
const SILENCE_BEEP_MS    = 60_000;  // 60s  → one beep
const SILENCE_NOTIFY_MS  = 120_000; // 2min → one browser notification
const SILENCE_COOLDOWN_MS = 300_000; // 5min → after full escalation, suppress beep/notify

// Play a short beep via Web Audio API oscillator (works without any audio file)
function playBeep(ctx: AudioContext, durationMs = 200, frequency = 880) {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
  } catch { /* swallow – non-critical */ }
}

// Session storage key for persisting user details across page refresh
const RECORDINGS_TABLE_USER_KEY = "recordingsTableUserDetails";

// Helper to get user details from sessionStorage
function getStoredUserDetails(): Record<string, any> | null {
  try {
    const stored = sessionStorage.getItem(RECORDINGS_TABLE_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export default function RecorderWav(): JSX.Element {
  const [state, setState] = useState<RecorderState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [wavUrl, setWavUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wavFile, setWavFile] = useState<Blob | null>(null);
  const [progress, setProgress] = useState(0);
  const [processingTitle, setProcessingTitle] = useState("");
  const [processingText, setProcessingText] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);

  // API base URL from config
  const [apiBaseUrl, setApiBaseUrl] = useState<string>("");

  // Streaming upload state - auto-detected based on backend availability
  const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, total: 0 });
  const [isStreaming, setIsStreaming] = useState<boolean | null>(null); // null = checking, true/false = result
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>('good');
  const streamingUploaderRef = useRef<StreamingUploader | null>(null);

  // Screen Wake Lock state - prevents screen from turning off during recording
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const [wakeLockSupported, setWakeLockSupported] = useState<boolean>(false);
  const [screenLockWarning, setScreenLockWarning] = useState<string | null>(null);

  // Audio playback ref
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Audio level monitoring (Web Audio API) — real input level indicator & silence detection
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const silenceBeeped = useRef(false);    // true after the one beep for this silence period
  const silenceNotified = useRef(false);  // true after the one notification for this period
  const lastFullEscalationRef = useRef(0); // timestamp of last notification (for cooldown)
  const smoothLevelRef = useRef(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [silenceWarning, setSilenceWarning] = useState(false);

  // Modal states
  const [showStartModal, setShowStartModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const wasPausedByModalRef = useRef(false);

  const location = useLocation();
  
  // Get stored user details as fallback for page refresh
  const storedDetails = getStoredUserDetails();
  const userId = location.state?.UsersId || storedDetails?.UsersId;
  const Guid = location.state?.Guid || storedDetails?.Guid;
  const patientName = location.state?.patientName || storedDetails?.patientName || "---";
  const patientPassport = location.state?.patientPassport || storedDetails?.patientPassport || "";
  const statusId = location.state?.StatusId ?? storedDetails?.StatusId;
  // appointmentId is state so it can be resolved from the backend on resume
  const [appointmentId, setAppointmentId] = useState<number | undefined>(
    location.state?.appointmentId ?? storedDetails?.appointmentId
  );
  const [appointmentStartTime, setAppointmentStartTime] = useState<string | undefined>(
    location.state?.appointmentStartTime ?? storedDetails?.appointmentStartTime
  );
  // Sync from location.state when navigating to the same route (component doesn't remount)
  useEffect(() => {
    if (location.state?.appointmentId !== undefined) {
      setAppointmentId(location.state.appointmentId);
    }
    if (location.state?.appointmentStartTime !== undefined) {
      setAppointmentStartTime(location.state.appointmentStartTime);
    }
  }, [location.state?.appointmentId, location.state?.appointmentStartTime]);
  const isPatientInactive = statusId !== undefined && statusId !== 1;
  const patientId = patientPassport || t("NO_ID") || "לא צוין";
  const appointmentDate = new Date().toLocaleDateString('he-IL');
  const appointmentTime = new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });

  // Resume session info (passed from PendingRecordingPopup)
  const resumeSession = location.state?.resumeSession as {
    sessionId: string;
    chunkCount: number;
    mimeType: string;
    durationSeconds: number;
  } | undefined;

  const [startProcessing] = useStartProcessingMutation();
  const [dismissOldSession] = useDismissRecordingSessionMutation();
  const [triggerGetOldSessions] = useLazyGetPendingRecordingSessionsQuery();
  const [finalizeRecoveredSession] = useFinalizeRecoveredSessionMutation();
  const startingRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const navigate = useNavigate();
  const timerRef = useRef<number | null>(null);
  const { setRecordingInProgress } = useActions();
  const currentTherapistId = useAppSelector(therapistIdSelector);

  // When resuming an interrupted session, fetch appointmentId from the backend
  // (source of truth) instead of relying on location.state which is lost on refresh.
  const appointmentFetchedRef = useRef(false);
  useEffect(() => {
    if (!resumeSession?.sessionId || !currentTherapistId) return;
    if (appointmentFetchedRef.current) return;
    appointmentFetchedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const result = await triggerGetOldSessions(Number(currentTherapistId)).unwrap();
        if (cancelled) return;
        if (result?.success && result.sessions?.length > 0) {
          const found = result.sessions.find(s => s.sessionId === resumeSession.sessionId);
          if (found?.appointmentId) {
            setAppointmentId(found.appointmentId);
            if (found.appointmentStartTime) {
              setAppointmentStartTime(found.appointmentStartTime);
            }
          }
        }
      } catch {
        // Non-critical — recording can proceed without appointment linkage
      }
    })();
    return () => { cancelled = true; };
  }, [resumeSession?.sessionId, currentTherapistId, triggerGetOldSessions]);

  // Guard: if no patient context is available (e.g., after company switch
  // cleared sessionStorage and location.state), redirect to patients list.
  useEffect(() => {
    if (!Guid && !userId) {
      navigate("/users", { replace: true });
    }
  }, [Guid, userId, navigate]);

  // --- Cross-tab recording guard & heartbeat ---
  // `amRecordingRef` is true while THIS component instance owns the recording.
  // It is never persisted — on refresh it resets to false, which is correct
  // because the heartbeat also stops on refresh (via beforeunload → pauseRecordingHeartbeat).
  const amRecordingRef = useRef(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start / stop heartbeat whenever amRecordingRef changes
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) return; // already running
    touchRecordingHeartbeat(); // immediate first beat
    heartbeatRef.current = setInterval(touchRecordingHeartbeat, HEARTBEAT_INTERVAL_MS);
  }, []);
  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  // On beforeunload (refresh / close): zero-out the heartbeat so the entry
  // is immediately stale when the new page loads.
  useEffect(() => {
    const handler = () => {
      if (amRecordingRef.current) {
        pauseRecordingHeartbeat();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Guard: block recording if another tab has a LIVE recording.
  // Since amRecordingRef is false on mount, getActiveRecording() returning
  // non-null means another tab is actively recording.
  const [crossTabBlock, setCrossTabBlock] = useState<string | null>(null);
  useEffect(() => {
    const active = getActiveRecording();
    if (active && !amRecordingRef.current) {
      setCrossTabBlock(
        `${t("RECORDING_ACTIVE_IN_OTHER_TAB")} (${active.patientName})`
      );
    }
  }, []);

  const { navigator: routerNavigator } = useContext(UNSAFE_NavigationContext);

  // Block in-app navigation while recording is active
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (state !== "recording" && state !== "paused") return;

    // Patch history.push and history.replace to intercept navigation
    const historyNav = routerNavigator as any;
    const originalPush = historyNav.push;
    const originalReplace = historyNav.replace;

    historyNav.push = (...args: any[]) => {
      pendingNavigationRef.current = () => originalPush.apply(historyNav, args);
      setShowLeaveModal(true);
    };

    historyNav.replace = (...args: any[]) => {
      pendingNavigationRef.current = () => originalReplace.apply(historyNav, args);
      setShowLeaveModal(true);
    };

    // Also handle browser back/forward buttons
    const handlePopState = (e: PopStateEvent) => {
      // Push the current state back so we stay on this page
      window.history.pushState(null, '', window.location.href);
      pendingNavigationRef.current = () => historyNav.go(-1);
      setShowLeaveModal(true);
    };

    // Push a state so we can intercept back
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      historyNav.push = originalPush;
      historyNav.replace = originalReplace;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [state, routerNavigator]);

  const confirmLeave = () => {
    setShowLeaveModal(false);
    setRecordingInProgress(false);
    amRecordingRef.current = false;
    stopHeartbeat();
    clearActiveRecording();
    // Let the navigation proceed — chunks remain on server for recovery
    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  };

  const cancelLeave = () => {
    setShowLeaveModal(false);
    pendingNavigationRef.current = null;
  };

  // Redirect to patient selection if no user context exists
  useEffect(() => {
    if (!userId || !Guid) {
      navigate('/recordMeeting', { replace: true });
    }
  }, [userId, Guid, navigate]);

  // Check if Screen Wake Lock API is supported
  useEffect(() => {
    setWakeLockSupported('wakeLock' in navigator);
  }, []);

  // Request Wake Lock to prevent screen from turning off during recording
  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) {
      // console.log('Wake Lock API not supported');
      return;
    }

    try {
      const wakeLock = (navigator as any).wakeLock as WakeLock;
      wakeLockRef.current = await wakeLock.request('screen');
      // console.log('Wake Lock acquired - screen will stay on');
      
      // Listen for wake lock release (e.g., when tab becomes hidden)
      wakeLockRef.current.addEventListener('release', () => {
        // console.log('Wake Lock was released');
        // Show warning when wake lock is released during recording
        if (state === "recording" || state === "paused") {
          setScreenLockWarning(t("SCREEN_LOCK_WARNING") || "המסך עשוי לכבות. שמור על האפליקציה פתוחה לתוצאות מיטביות.");
        }
      });
    } catch (err: any) {
      // Wake lock request failed - usually due to low battery or page not visible
      console.warn('Wake Lock request failed:', err.message);
    }
  }, [state]);

  // Release Wake Lock
  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        // console.log('Wake Lock released');
      } catch (err) {
        console.warn('Wake Lock release failed:', err);
      }
    }
    setScreenLockWarning(null);
  }, []);

  // Re-acquire wake lock when page becomes visible again
  const handleVisibilityChangeForWakeLock = useCallback(async () => {
    if (!document.hidden && (state === "recording" || state === "paused")) {
      // Page became visible and we're recording - try to re-acquire wake lock
      if (!wakeLockRef.current || wakeLockRef.current.released) {
        await requestWakeLock();
        setScreenLockWarning(null); // Clear warning when wake lock is re-acquired
      }
    }
  }, [state, requestWakeLock]);

  // Load config and check if streaming upload is available on the backend.
  // If we're offline when this runs, it will fail — so we also retry on the
  // browser's 'online' event to recover.
  useEffect(() => {
    const initConfig = async () => {
      try {
        const config = await loadAppConfig();
        const baseUrl = resolveApiBaseUrl(config).replace(/\/$/, ''); // Remove trailing slash
        setApiBaseUrl(baseUrl);
        
        // Check if streaming is available
        const available = await isStreamingAvailable(baseUrl);
        setIsStreaming(available);
      } catch (err) {
        console.warn('[MeetingRecording] initConfig failed (likely offline) — will retry when online');
        // Don't set isStreaming(false) — leave it null so startRecording knows it was never resolved
      }
    };

    initConfig();

    // Retry when browser comes back online (handles the "went offline before page loaded" case)
    const retryOnOnline = () => {
   // console.log('[MeetingRecording] Online — retrying streaming config...');
      initConfig();
    };
    window.addEventListener('online', retryOnOnline);
    return () => window.removeEventListener('online', retryOnOnline);
  }, []);

  // Browser-level online/offline detection — independent of the streaming uploader.
  // Catches the case where the user is already offline when recording starts, or
  // goes offline before any chunk upload has been attempted.
  useEffect(() => {
    // Seed from current browser state
    if (!navigator.onLine) {
      setConnectionQuality('offline');
    }

    const goOffline = () => setConnectionQuality('offline');
    const goOnline = () => {
      // When connectivity returns, only upgrade from 'offline' — let the uploader
      // refine the quality further once actual uploads succeed.
      setConnectionQuality((prev) => (prev === 'offline' ? 'good' : prev));
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  // When resuming a recovered session, show "paused" state with previous duration
  // The user clicks "Continue" to actually start the mic and resume recording
  const resumeTriggeredRef = useRef(false);
  useEffect(() => {
    if (resumeSession && !resumeTriggeredRef.current && state === "idle") {
      // Guard: don't claim if another tab already has a live recording
      const active = getActiveRecording();
      if (active) {
        setCrossTabBlock(
          `${t("RECORDING_ACTIVE_IN_OTHER_TAB")} (${active.patientName})`
        );
        return;
      }
      resumeTriggeredRef.current = true;
      setSeconds(resumeSession.durationSeconds || 0);
      setState("paused");
      setRecordingInProgress(true);
      amRecordingRef.current = true;
      setActiveRecording({
        sessionId: resumeSession.sessionId,
        patientGuid: Guid || '',
        userId: userId?.toString() || '',
        patientName: patientName || '',
      });
      startHeartbeat();
    }
  }, [resumeSession, state]);

  // Warn user before closing browser/tab during active recording
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only warn if recording is genuinely in progress (not when blocked by crossTabBlock)
      if (crossTabBlock) return;
      if (state === "recording" || state === "paused" || state === "processing") {
        // Standard way to trigger browser's "Leave site?" dialog
        e.preventDefault();
        // For older browsers
        e.returnValue = '';
        return '';
      }
    };

    // Handle visibility change (screen off, app switch on mobile)
    const handleVisibilityChange = () => {
      if (document.hidden && state === "recording") {
        // Show notification only when actively recording (not when paused)
        setScreenLockWarning(t("RECORDING_INTERRUPTED_WARNING") || "ההקלטה עשויה להפסיק כאשר המסך כבוי. אנא חזור לאפליקציה.");
        
        // Request notification permission and show notification if supported
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(t("RECORDING_IN_PROGRESS") || "הקלטה בתהליך", {
            body: t("RETURN_TO_APP_TO_CONTINUE") || "חזור לאפליקציה כדי להמשיך בהקלטה",
            icon: '/favicon.ico',
            tag: 'recording-warning',
            requireInteraction: true
          });
        }
      } else if (!document.hidden) {
        // Page became visible - try to re-acquire wake lock
        handleVisibilityChangeForWakeLock();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state, crossTabBlock, handleVisibilityChangeForWakeLock]);

  // --- Helpers ---
  const formatTime = (s: number) => {
    const hh = Math.floor(s / 3600).toString().padStart(2, "0");
    const mm = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
    const ss = Math.floor(s % 60).toString().padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  // --- Get file extension based on mime type ---
  const getFileExtension = (mimeType: string): string => {
    if (mimeType.includes('webm')) return '.webm';
    if (mimeType.includes('ogg')) return '.ogg';
    if (mimeType.includes('mp4')) return '.m4a';
    return '.wav';
  };

  // --- Get supported MIME type for recording ---

  // --- Audio level monitoring (Web Audio API) ---
  const runAudioMonitorLoop = useCallback(() => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;
    const bufLen = analyser.fftSize;
    const dataArray = new Uint8Array(bufLen);

    const tick = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < bufLen; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / bufLen);
      // Map RMS (typically 0–0.25 for speech) to a 0–100 scale
      const rawLevel = Math.min(100, rms * 400);
      // Exponential moving average: fast attack, slow decay → smooth wave
      const alpha = rawLevel > smoothLevelRef.current ? 0.4 : 0.15;
      smoothLevelRef.current += alpha * (rawLevel - smoothLevelRef.current);
      const level = Math.round(smoothLevelRef.current);
      setAudioLevel(level);

      // Silence detection – progressive escalation
      if (rms < SILENCE_THRESHOLD) {
        const now = Date.now();
        if (!silenceStartRef.current) {
          silenceStartRef.current = now;
        }
        const elapsed = now - silenceStartRef.current;
        const inCooldown = now - lastFullEscalationRef.current < SILENCE_COOLDOWN_MS;

        // Stage 1: visual banner at 30s
        if (elapsed >= SILENCE_BANNER_MS) {
          setSilenceWarning(true);
        }

        // Stage 2: one beep at 60s (skip if in cooldown)
        if (elapsed >= SILENCE_BEEP_MS && !silenceBeeped.current && !inCooldown && audioContextRef.current) {
          silenceBeeped.current = true;
          playBeep(audioContextRef.current);
        }

        // Stage 3: one browser notification at 2min (skip if in cooldown)
        if (elapsed >= SILENCE_NOTIFY_MS && !silenceNotified.current && !inCooldown) {
          silenceNotified.current = true;
          lastFullEscalationRef.current = now;
          if ('Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification(t("NO_AUDIO_ALERT_TITLE") || "\u26A0\uFE0F \u05DC\u05D0 \u05D6\u05D5\u05D4\u05D4 \u05E7\u05DC\u05D8 \u05E9\u05DE\u05E2", {
                body: t("NO_AUDIO_ALERT_BODY") || "\u05DC\u05D0 \u05E0\u05E7\u05DC\u05D8 \u05E9\u05DE\u05E2 \u05DE\u05D4\u05DE\u05D9\u05E7\u05E8\u05D5\u05E4\u05D5\u05DF. \u05D1\u05D3\u05D5\u05E7 \u05E9\u05D4\u05DE\u05D9\u05E7\u05E8\u05D5\u05E4\u05D5\u05DF \u05E4\u05E2\u05D9\u05DC \u05D5\u05D0\u05D9\u05E0\u05D5 \u05DE\u05D5\u05E9\u05EA\u05E7.",
                icon: '/favicon.ico',
                tag: 'silence-warning',
                requireInteraction: false,
              });
            } catch { /* non-critical */ }
          }
        }
      } else {
        // Audio returned — reset silence tracking (cooldown persists via lastFullEscalationRef)
        silenceStartRef.current = null;
        silenceBeeped.current = false;
        silenceNotified.current = false;
        setSilenceWarning(false);
      }

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  const startAudioMonitoring = useCallback((stream: MediaStream) => {
    // Clean up previous monitoring if any
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current) audioContextRef.current.close().catch(() => {});

    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.5;
    source.connect(analyser);

    audioContextRef.current = ctx;
    analyserRef.current = analyser;
    silenceStartRef.current = null;
    silenceBeeped.current = false;
    silenceNotified.current = false;
    setSilenceWarning(false);

    runAudioMonitorLoop();
  }, [runAudioMonitorLoop]);

  const pauseAudioMonitoring = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    smoothLevelRef.current = 0;
    setAudioLevel(0);
    silenceStartRef.current = null;
    silenceBeeped.current = false;
    silenceNotified.current = false;
    setSilenceWarning(false);
  }, []);

  const stopAudioMonitoring = useCallback(() => {
    pauseAudioMonitoring();
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, [pauseAudioMonitoring]);

  // --- Start recording ---
  const startRecording = async () => {
    if (startingRef.current) return;
    startingRef.current = true;

    setError(null);

    // Guard: verify no other recording has claimed the lock since we mounted
    const active = getActiveRecording();
    if (active && !amRecordingRef.current) {
      startingRef.current = false;
      setCrossTabBlock(
        `${t("RECORDING_ACTIVE_IN_OTHER_TAB")} (${active.patientName})`
      );
      return;
    }

    try {
      // If this is a fresh recording (not a resume), dismiss any old active sessions
      // for THIS PATIENT ONLY — other patients' sessions should be preserved
      if (!resumeSession) {
        try {
          const result = await triggerGetOldSessions(Number(currentTherapistId)).unwrap();
          if (result?.success && result.sessions?.length > 0) {
            for (const oldSession of result.sessions) {
              // Only dismiss sessions belonging to the same patient
              if (oldSession.patientGuid === Guid || oldSession.patientGuid === Guid) {
                await dismissOldSession(oldSession.sessionId).unwrap().catch(() => {});
              }
            }
          }
        } catch {
          // Non-critical - continue with recording
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Start real-time audio level monitoring
      startAudioMonitoring(stream);

      const mimeType = getSupportedRecordingMimeType();
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      // Initialize streaming uploader.
      // When offline at page load, isStreaming may be null (check failed) and apiBaseUrl may be empty.
      // We still create the uploader — chunks are saved to IndexedDB locally and uploaded when online.
      // Resolve apiBaseUrl on the spot if the config load succeeded earlier (it caches).
      let effectiveApiBaseUrl = apiBaseUrl;
      if (!effectiveApiBaseUrl) {
        try {
          const config = await loadAppConfig();
          effectiveApiBaseUrl = resolveApiBaseUrl(config).replace(/\/$/, '');
          setApiBaseUrl(effectiveApiBaseUrl);
        } catch {
          // config.json is a local file served by the SPA — should almost always be available
        }
      }

      // isStreaming: true = confirmed, null = check failed (e.g. offline), false = server said no
      const shouldStream = isStreaming !== false && userId && effectiveApiBaseUrl;
      if (shouldStream) {
        const uploader = new StreamingUploader({
          apiBaseUrl: effectiveApiBaseUrl,
          userId: userId.toString(),
          patientName: patientName,
          patientGuid: Guid,
          appointmentId: appointmentId,
          // Resume existing session if coming from recovery popup
          resumeSessionId: resumeSession?.sessionId,
          resumeChunkIndex: resumeSession?.chunkCount,
          onProgress: (uploaded, total) => {
            setUploadProgress({ uploaded, total });
          },
          onError: (err, chunkIndex) => {
            // Chunk failed, will retry
          },
          onChunkUploaded: (chunkIndex) => {
            // Chunk uploaded successfully
          },
          onConnectionQuality: (quality) => {
            setConnectionQuality(quality);
          },
        });
        uploader.setMimeType(mimeType || 'audio/webm');
        streamingUploaderRef.current = uploader;
        setConnectionQuality(navigator.onLine ? 'good' : 'offline');

        // Register this recording in the cross-tab registry.
        // Set amRecordingRef immediately so beforeunload will zero the heartbeat
        // if the user refreshes before setState("recording") is reached.
        amRecordingRef.current = true;
        setActiveRecording({
          sessionId: uploader.getSessionId(),
          patientGuid: Guid || '',
          userId: userId?.toString() || '',
          patientName: patientName || '',
        });
      }

      mediaRecorder.ondataavailable = async (e: BlobEvent) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
          
          // Stream chunk to server in real-time
          if (streamingUploaderRef.current) {
            try {
              await streamingUploaderRef.current.addChunk(e.data);
            } catch (err) {
              // Failed to queue chunk - will retry
            }
          }
        }
      };

      // Collect data every 2 seconds for streaming (balance between latency and overhead)
      mediaRecorder.start(2000);

      // If resuming, start from the previous duration; otherwise from 0
      const initialSeconds = resumeSession?.durationSeconds || 0;
      setSeconds(initialSeconds);
      timerRef.current = window.setInterval(() => setSeconds((s) => {
        const newSeconds = s + 1;
        // Keep streaming uploader duration in sync for server-side session tracking
        if (streamingUploaderRef.current) {
          streamingUploaderRef.current.setDurationSeconds(newSeconds);
        }
        return newSeconds;
      }), 1000);

      // Request wake lock to prevent screen from turning off during recording
      await requestWakeLock();
      
      // Request notification permission for background warnings
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }

      setState("recording");
      setRecordingInProgress(true);
      // For non-streaming recordings, register with a local-only session ID.
      // amRecordingRef is already true for the streaming path (set above).
      if (!streamingUploaderRef.current) {
        amRecordingRef.current = true;
        setActiveRecording({
          sessionId: `local_${Date.now()}`,
          patientGuid: Guid || '',
          userId: userId?.toString() || '',
          patientName: patientName || '',
        });
      }
      startHeartbeat();
    } catch (err: any) {
      startingRef.current = false;
      setError(t("MICROPHONE_CONNECTION_FAILED"));
    }
  };

  // --- Pause recording ---
  const pauseRecording = () => {
    if (state === "recording" && mediaRecorderRef.current) {
      mediaRecorderRef.current.pause();
      pauseAudioMonitoring();
      setState("paused");
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // --- Resume recording ---
  const resumeRecording = () => {
    if (state !== "paused") return;

    if (mediaRecorderRef.current) {
      // Normal pause/resume within the same recording session
      mediaRecorderRef.current.resume();
      runAudioMonitorLoop(); // Resume real-time audio monitoring
      setState("recording");
      timerRef.current = window.setInterval(() => setSeconds((s) => {
        const newSeconds = s + 1;
        if (streamingUploaderRef.current) {
          streamingUploaderRef.current.setDurationSeconds(newSeconds);
        }
        return newSeconds;
      }), 1000);
    } else {
      // Resuming a recovered session — no active MediaRecorder yet
      // Start a fresh MediaRecorder that continues the same server session
      startRecording();
    }
  };

  // --- Stop recording and finalize ---
  const stopRecording = (): Promise<void> => {
    return new Promise((resolve) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Stop audio level monitoring
      stopAudioMonitoring();

      // Release wake lock when recording stops
      releaseWakeLock();

      const mediaRecorder = mediaRecorderRef.current;
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.onstop = () => {
          const mimeType = mediaRecorder.mimeType || 'audio/webm';
          const blob = new Blob(recordedChunksRef.current, { type: mimeType });
          setWavFile(blob);
          const url = URL.createObjectURL(blob);
          setWavUrl(url);

          // Stop the media stream
          const stream = mediaStreamRef.current;
          if (stream) {
            stream.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
          }
          mediaRecorderRef.current = null;

          setState("finished");
          setRecordingInProgress(false);
          amRecordingRef.current = false;
          stopHeartbeat();
          clearActiveRecording();
          resolve();
        };
        mediaRecorder.stop();
      } else {
        // No active recorder, just clean up
        const stream = mediaStreamRef.current;
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
          mediaStreamRef.current = null;
        }
        resolve();
      }
    });
  };

  // --- Upload file via legacy (non-streaming) endpoint ---
  const uploadViaLegacy = (file: Blob) => {
    const blobType = file.type || 'audio/webm';
    const fileName = `recording${getFileExtension(blobType)}`;

    setProcessingTitle(t("UPLOADING_RECORDING") || "מעלה הקלטה...");
    setProcessingText(t("TRANSFERRING_FILE") || "מעביר קובץ לשרת");

    startProcessing({
      file: new File([file], fileName, { type: blobType }),
      usersGuid: Guid,
      type: 5,
    }).then((response) => {
      if ("data" in response && response.data?.success) {
        navigate(`/users/recordTable`, {
          state: {
            Guid: Guid,
            UsersId: userId,
            patientName: patientName,
            newRecordNumber: response.data.recordNumber,
          },
        });
      } else {
        setError(t("UPLOAD_FAILED") || "Upload failed. Please try again.");
        setState("finished");
      }
    }).catch(() => {
      setError(t("UPLOAD_FAILED") || "Upload failed. Please try again.");
      setState("finished");
    });
  };

  // --- Handle save and upload (async workflow) ---
  const handleSaveAndUpload = async () => {
    if (!wavUrl || !wavFile) return;

    if (!userId) {
      setError(t("UPLOAD_FAILED") || "User ID is missing. Please try again.");
      return;
    }

    setState("processing");

    // Check if we're using streaming upload
    if (streamingUploaderRef.current) {
      setProcessingTitle(t("FINALIZING_UPLOAD") || "משלים העלאה...");
      setProcessingText(t("ASSEMBLING_RECORDING") || "מרכיב את ההקלטה");
      setProgress(80); // Most upload already done during recording

      try {
        // Wait for any remaining chunks to upload
        await streamingUploaderRef.current.waitForUploads();
        setProgress(90);

        // Finalize the session
        const result = await streamingUploaderRef.current.finalize();

        if (result.success) {
          setProgress(100);
          // Mark session as finalized in localStorage so other tabs skip it
          try {
            const finalized = JSON.parse(localStorage.getItem('finalizedSessions') || '[]');
            finalized.push(streamingUploaderRef.current.getSessionId());
            localStorage.setItem('finalizedSessions', JSON.stringify(finalized.slice(-20)));
          } catch { /* non-critical */ }
          // Redirect to records table
          navigate(`/users/recordTable`, {
            state: {
              Guid: Guid,
              UsersId: userId,
              patientName: patientName,
              newRecordNumber: result.recordNumber,
            },
          });
        } else {
          throw new Error(result.error || "Finalization failed");
        }
      } catch (err: any) {
        setError(t("UPLOAD_FAILED") || "Upload failed. Please try again.");
        setState("finished");
        // Note: chunks are still in IndexedDB for retry
      }
      return;
    }

    // Legacy upload (fallback when streaming is disabled)
    uploadViaLegacy(wavFile);
  };

  // --- Simulate processing stages ---
  const simulateProcessing = () => {
    const stages: ProcessingStage[] = [
      { end: 30, titleKey: "UPLOADING_RECORDING", textKey: "TRANSFERRING_FILE" },
      { end: 60, titleKey: "TRANSCRIBING_MEETING", textKey: "IDENTIFYING_SPEECH" },
      { end: 90, titleKey: "SUMMARIZING_MEETING", textKey: "ANALYZING_CONTENT" },
      { end: 100, titleKey: "FINISHING", textKey: "SAVING_DATA" },
    ];

    let currentProgress = 0;
    let currentStage = 0;

    setProcessingTitle(t(stages[0].titleKey) || stages[0].titleKey);
    setProcessingText(t(stages[0].textKey) || stages[0].textKey);

    const interval = setInterval(() => {
      currentProgress += 2;
      setProgress(currentProgress);

      if (currentStage < stages.length && currentProgress >= stages[currentStage].end) {
        if (currentStage + 1 < stages.length) {
          setProcessingTitle(t(stages[currentStage + 1].titleKey) || stages[currentStage + 1].titleKey);
          setProcessingText(t(stages[currentStage + 1].textKey) || stages[currentStage + 1].textKey);
        }
        currentStage++;
      }

      if (currentProgress >= 100) {
        clearInterval(interval);
      }
    }, 100);
  };

  // --- Reset recording ---
  const resetRecording = async () => {
    // Cancel streaming upload if active
    if (streamingUploaderRef.current) {
      try {
        await streamingUploaderRef.current.cancel();
      } catch (err) {
        // Failed to cancel session - server will clean up
      }
      streamingUploaderRef.current = null;
    } else if (resumeSession?.sessionId) {
      // We're in the resumed-paused state (no uploader created yet).
      // Directly call the server dismiss endpoint to clean up the old session.
      try {
        await dismissOldSession(resumeSession.sessionId).unwrap();
      } catch {
        // Non-critical — cleanup service will handle it eventually
      }
    }

    if (wavUrl) {
      URL.revokeObjectURL(wavUrl);
      setWavUrl(null);
    }
    recordedChunksRef.current = [];
    setSeconds(0);
    setProgress(0);
    setUploadProgress({ uploaded: 0, total: 0 });
    setState("idle");
    setRecordingInProgress(false);
    amRecordingRef.current = false;
    stopHeartbeat();
    clearActiveRecording();
    setError(null);
    setWavFile(null);
  };

  // --- Modal handlers ---
  const handleStartClick = () => {
    if (isPatientInactive) {
      setShowInactiveModal(true);
      return;
    }
    setShowStartModal(true);
  };

  const confirmStart = () => {
    setShowStartModal(false);
    startRecording();
  };

  const handleStopClick = () => {
    // Pause recording while showing the modal
    if (state === "recording" && mediaRecorderRef.current) {
      mediaRecorderRef.current.pause();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      wasPausedByModalRef.current = true;
    } else {
      wasPausedByModalRef.current = false;
    }
    setShowStopModal(true);
  };

  const confirmStop = async () => {
    setShowStopModal(false);
    
    // If streaming upload is active, finalize immediately (chunks already on server)
    if (streamingUploaderRef.current) {
      // Stop audio monitoring and media recorder
      stopAudioMonitoring();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      const mediaRecorder = mediaRecorderRef.current;
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        // Request final data chunk
        mediaRecorder.stop();
      }
      
      // Stop media stream
      const stream = mediaStreamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      mediaRecorderRef.current = null;

      // Show processing state
      setState("processing");
      setRecordingInProgress(false);
      amRecordingRef.current = false;
      stopHeartbeat();
      clearActiveRecording();
      
      // Get initial upload status
      const initialStatus = streamingUploaderRef.current.getUploadStatus();
      const pendingChunks = initialStatus.pending;
      
      if (pendingChunks > 0) {
        setProcessingTitle(t("UPLOADING_REMAINING") || "מעלה חלקים שנותרו...");
        setProcessingText(`${pendingChunks} ${t("CHUNKS_REMAINING") || "חלקים נותרו להעלאה"}`);
        setProgress(50);
      } else {
        setProcessingTitle(t("FINALIZING_UPLOAD") || "משלים העלאה...");
        setProcessingText(t("ASSEMBLING_RECORDING") || "מרכיב את ההקלטה");
        setProgress(80);
      }

      try {
        // Wait a moment for the last chunk to be queued
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Wait for any remaining chunks to upload with progress tracking
        await streamingUploaderRef.current.waitForUploadsWithProgress((uploaded, total) => {
          const uploadPercent = total > 0 ? (uploaded / total) * 100 : 100;
          const progressValue = 50 + (uploadPercent * 0.35); // 50-85%
          setProgress(Math.round(progressValue));
          const remaining = total - uploaded;
          if (remaining > 0) {
            setProcessingText(`${remaining} ${t("CHUNKS_REMAINING") || "חלקים נותרו להעלאה"}`);
          } else {
            setProcessingText(t("ASSEMBLING_RECORDING") || "מרכיב את ההקלטה");
          }
        });
        setProgress(85);

        // Upload any pending chunks that might have failed
        await streamingUploaderRef.current.uploadPendingChunks();
        setProgress(90);

        // Finalize the session - server assembles chunks and starts processing
        const result = await streamingUploaderRef.current.finalize();
        
        if (result.success) {
          setProgress(100);
          // Mark session as finalized in localStorage so other tabs skip it
          try {
            const finalized = JSON.parse(localStorage.getItem('finalizedSessions') || '[]');
            finalized.push(result.recordNumber ? streamingUploaderRef.current.getSessionId() : '');
            // Keep only last 20 entries
            localStorage.setItem('finalizedSessions', JSON.stringify(finalized.slice(-20)));
          } catch { /* non-critical */ }
          // Cleanup the uploader
          streamingUploaderRef.current.cleanup();
          streamingUploaderRef.current = null;
          
          // Redirect to records table
          navigate(`/users/recordTable`, {
            state: {
              Guid: Guid,
              UsersId: userId,
              patientName: patientName,
              newRecordNumber: result.recordNumber,
            },
          });
        } else {
          throw new Error(result.error || "Finalization failed");
        }
      } catch (err: any) {
        setError(t("UPLOAD_FAILED") || "העלאה נכשלה. אנא נסה שוב.");
        setState("idle");
        // Note: chunks are still in IndexedDB for retry
      }
      return;
    }

    // Resumed session with no new recording started — finalize existing server-side chunks directly
    if (resumeSession?.sessionId) {
      setState("processing");
      setRecordingInProgress(false);
      amRecordingRef.current = false;
      stopHeartbeat();
      clearActiveRecording();
      setProcessingTitle(t("FINALIZING_UPLOAD"));
      setProcessingText(t("ASSEMBLING_RECORDING"));
      setProgress(80);
      try {
        const result = await finalizeRecoveredSession({
          sessionId: resumeSession.sessionId,
          userGuid: Guid || '',
          totalChunks: resumeSession.chunkCount,
          mimeType: resumeSession.mimeType,
        }).unwrap();
        if (result.success && result.recordNumber) {
          setProgress(100);
          navigate(`/users/recordTable`, {
            state: {
              Guid: Guid,
              UsersId: userId,
              patientName: patientName,
              newRecordNumber: result.recordNumber,
            },
          });
        } else {
          throw new Error(result.message || "Finalization failed");
        }
      } catch {
        setError(t("UPLOAD_FAILED"));
        setState("paused");
      }
      return;
    }

    // Fallback to old flow (non-streaming) - stop and show preview
    await stopRecording();
  };

  // --- Play/Pause audio ---
  const togglePlayback = () => {
    if (!wavUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setIsPlaying(false);
    }

    // Set src if not set or different
    if (audioRef.current.src !== wavUrl) {
      audioRef.current.src = wavUrl;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // --- Upload recording (async workflow) ---
  const handleUpload = () => {
    if (!wavFile) return;

    // Stop playback if playing
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    if (!Guid) {
      setError(t("UPLOAD_FAILED") || "User ID is missing. Please try again.");
      return;
    }

    setState("processing");
    uploadViaLegacy(wavFile);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    setShowCancelModal(false);
    stopAudioMonitoring();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Stop media stream
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    await resetRecording();
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop audio level monitoring
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
      if (timerRef.current) clearInterval(timerRef.current);
      if (wavUrl) URL.revokeObjectURL(wavUrl);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Release wake lock on unmount
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
      // Re-enable inactivity timer on unmount
      setRecordingInProgress(false);
      amRecordingRef.current = false;
      stopHeartbeat();
      clearActiveRecording();
      // Note: We intentionally DON'T cancel the streaming uploader on unmount
      // This allows chunks to be recovered via IndexedDB if user navigates away
      // The server will clean up orphaned sessions after timeout
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for incomplete sessions on mount (for recovery after browser close)
  useEffect(() => {
    const checkIncompleteSessions = async () => {
      try {
        const sessions = await getIncompleteSessions();
        if (sessions.length > 0) {
          // TODO: Could show a recovery UI here
        }
      } catch (err) {
        // Failed to check incomplete sessions
      }
    };
    checkIncompleteSessions();
  }, []);

  const isRecording = state === "recording";
  const isPaused = state === "paused";
  const isProcessing = state === "processing";
  const isFinished = state === "finished";

  // Block UI if another tab has an active recording
  if (crossTabBlock) {
    // Use window.location for navigation because React Router's history may be
    // patched by the "block navigation during recording" interceptor (state === "paused").
    const goBack = () => { window.location.replace("/recordMeeting"); };
    return (
      <div className={styles.recRoot} dir={i18n.dir()}>
        <Modal2 onClose={goBack}>
          <div style={{ padding: "30px", textAlign: "center", maxWidth: "400px" }}>
            <p style={{ fontSize: "1.2rem", marginBottom: "24px" }}>
              {crossTabBlock}
            </p>
            <button
              className="button hover"
              style={{ width: "180px" }}
              onClick={goBack}
            >
              {t("BACK") || "חזור"}
            </button>
          </div>
        </Modal2>
      </div>
    );
  }

  return (
    <div className={styles.recRoot} dir={i18n.dir()}>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          {/* Back Button */}
          <div className={styles.rtTopbar}>
            <BackArrowButton
              text={t("BACK") || "חזור"}
              onClick={isRecording || isPaused || isProcessing ? undefined : () => navigate(-1)}
              position={"center"}
            />
          </div>

          {/* Patient Info */}
          <div className={styles.patientInfo}>
            <h2>👤 {t("PATIENT_DETAILS") || "פרטי המטופל"}</h2>
            <div className={styles.patientDetails}>
              <div><strong>{t("PATIENT_NAME") || "שם"}:</strong> {patientName}</div>
              <div><strong>{t("PATIENT_DATE") || "תאריך"}:</strong> {appointmentDate}</div>
              <div><strong>{t("PATIENT_TIME") || "שעה"}:</strong> {appointmentTime}</div>
              <div><strong>{t("PATIENT_ID") || "ת.ז"}:</strong> {patientId}</div>
              <div>
                <strong>{t("RECORDING_LINKAGE") || "שיוך פגישה"}:</strong>{" "}
                {appointmentId
                  ? `${t("LINKED_TO_APPOINTMENT") || "משויך לתור"} ${appointmentStartTime ? `(${appointmentStartTime})` : ""}`
                  : t("NOT_LINKED") || "לא משויך"
                }
              </div>
            </div>
          </div>

          {/* Recording Section */}
          {!isProcessing && !isFinished && (
            <div className={styles.recordingSection}>
              <h2>🎙️ {t("MEETING_RECORDING_TITLE") || "הקלטת פגישה"}</h2>

              <div className={`${styles.timer} ${isRecording ? styles.recording : ""} ${isPaused ? styles.paused : ""}`}>
                {formatTime(seconds)}
              </div>

              {/* Audio Input Level — wave visualiser */}
              {isRecording && (
                <div className={styles.audioLevelContainer}>
                  <div className={styles.audioLevelLabel}>
                    🎤 {t("AUDIO_INPUT") || "קלט שמע"}
                  </div>
                  <div className={styles.audioLevelMeter}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`${styles.waveBar} ${
                          audioLevel > 50 ? styles.waveHigh :
                          audioLevel > 12 ? styles.waveMid :
                          audioLevel > 2  ? styles.waveIdle :
                          styles.waveNone
                        }`}
                        style={{
                          animationDelay: `${i * 0.12}s`,
                          ['--wave-h' as any]: audioLevel > 3
                            ? `${4 + audioLevel * [0.35, 0.55, 0.7, 0.55, 0.35][i]}px`
                            : undefined,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Silence Warning — no audio detected for extended period */}
              {silenceWarning && isRecording && (
                <div className={styles.silenceWarning}>
                  <span className={styles.warningIcon}>⚠️</span>
                  <span className={styles.warningText}>
                    {t("NO_AUDIO_DETECTED") || "לא זוהה קלט שמע — בדוק שהמיקרופון פעיל ואינו מושתק"}
                  </span>
                  <button
                    className={styles.dismissWarning}
                    onClick={() => { setSilenceWarning(false); silenceStartRef.current = Date.now(); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', marginInlineStart: '10px' }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Signal Meter - Connection Quality Indicator */}
              {(isRecording || isPaused) && (
                <div className={styles.signalMeterContainer}>
                  <div className={styles.signalMeter}>
                    <div className={`${styles.signalBar} ${styles.bar1} ${connectionQuality !== 'offline' ? styles.active : ''}`}></div>
                    <div className={`${styles.signalBar} ${styles.bar2} ${connectionQuality === 'good' || connectionQuality === 'slow' ? styles.active : ''}`}></div>
                    <div className={`${styles.signalBar} ${styles.bar3} ${connectionQuality === 'good' ? styles.active : ''}`}></div>
                    <div className={`${styles.signalBar} ${styles.bar4} ${connectionQuality === 'good' ? styles.active : ''}`}></div>
                  </div>
                  <div className={`${styles.signalLabel} ${styles[connectionQuality]}`}>
                    {connectionQuality === 'good' && (t("CONNECTION_GOOD") || "חיבור תקין")}
                    {connectionQuality === 'slow' && (t("CONNECTION_SLOW") || "חיבור איטי")}
                    {connectionQuality === 'poor' && (t("CONNECTION_POOR") || "חיבור חלש")}
                    {connectionQuality === 'offline' && (t("CONNECTION_OFFLINE") || "אין חיבור")}
                  </div>
                  {uploadProgress.total > 0 && (
                    <div className={styles.uploadProgress}>
                      <span className={styles.uploadProgressText}>
                        {uploadProgress.uploaded}/{uploadProgress.total} {t("CHUNKS") || "חלקים"}
                      </span>
                      <div className={styles.uploadProgressBar}>
                        <div 
                          className={styles.uploadProgressFill} 
                          style={{ width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Connection Quality Warning */}
              {(isRecording || isPaused) && (connectionQuality === 'poor' || connectionQuality === 'offline') && (
                <div className={styles.connectionWarning}>
                  <span className={styles.warningIcon}>⚠️</span>
                  <span className={styles.warningText}>
                    {connectionQuality === 'offline' 
                      ? t("OFFLINE_WARNING") || "אין חיבור לאינטרנט. ההקלטה נשמרת במכשיר ותועלה כשהחיבור יחזור."
                      : t("SLOW_WARNING") || "החיבור איטי. המשך להקליט - הנתונים יועלו ברקע."}
                  </span>
                </div>
              )}

              {/* Screen Lock Warning */}
              {screenLockWarning && (isRecording || isPaused) && (
                <div className={styles.connectionWarning} style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
                  <span className={styles.warningIcon}>📱</span>
                  <span className={styles.warningText}>
                    {screenLockWarning}
                  </span>
                  <button 
                    className={styles.dismissWarning}
                    onClick={() => setScreenLockWarning(null)}
                    style={{ 
                      marginRight: '10px', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Wake Lock Info - show on mobile when not supported */}
              {!wakeLockSupported && (isRecording || isPaused) && (
                <div className={styles.connectionWarning} style={{ backgroundColor: '#e7f3ff', borderColor: '#2196f3' }}>
                  <span className={styles.warningIcon}>ℹ️</span>
                  <span className={styles.warningText}>
                    {t("KEEP_SCREEN_ON_TIP") || "טיפ: שמור על המסך דלוק כדי למנוע הפסקת ההקלטה"}
                  </span>
                </div>
              )}

              {/* Recording Controls */}
              <div className={styles.recordingControls}>
                {state === "idle" && (
                  <button
                    className={styles.recordButton}
                    onClick={handleStartClick}
                    style={isPatientInactive ? { opacity: 0.5, filter: "grayscale(0.4)" } : undefined}
                  >
                    <span className={styles.icon}>🎙️</span>
                    <span>{t("START_RECORDING") || "התחל הקלטה"}</span>
                  </button>
                )}

                {(isRecording || isPaused) && (
                  <button
                    className={`${styles.recordButton} ${isRecording ? styles.recording : styles.paused}`}
                    disabled
                  >
                    <span className={styles.icon}>🎙️</span>
                    <span>{isRecording ? (t("RECORDING_IN_PROGRESS") || "מקליט...") : (t("PAUSE_RECORDING") || "מושהה")}</span>
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div className={`${styles.actionButtons} ${(isRecording || isPaused) ? styles.active : ""}`}>
                {isRecording && (
                  <button className={`${styles.btn} ${styles.btnPause}`} onClick={pauseRecording}>
                    <span className={styles.icon}>⏸️</span>
                    <span>{t("PAUSE_RECORDING") || "השהה"}</span>
                  </button>
                )}
                {isPaused && (
                  <button className={`${styles.btn} ${styles.btnContinue}`} onClick={resumeRecording}>
                    <span className={styles.icon}>▶️</span>
                    <span>{t("CONTINUE_RECORDING") || "המשך"}</span>
                  </button>
                )}
                <button className={`${styles.btn} ${styles.btnStop}`} onClick={handleStopClick}>
                  <span className={styles.icon}>⏹️</span>
                  <span>{t("FINISH_AND_UPLOAD") || "סיים והעלה"}</span>
                </button>
                <button className={`${styles.btn} ${styles.btnCancel}`} onClick={handleCancelClick}>
                  <span className={styles.icon}>🗑️</span>
                  <span>{t("CANCEL_RECORDING") || "ביטול"}</span>
                </button>
              </div>
            </div>
          )}

          {/* Finished Section - Preview and Upload */}
          {isFinished && (
            <div className={styles.recordingSection}>
              <h2>✅ {t("RECORDING_COMPLETE") || "ההקלטה הושלמה"}</h2>

              <div className={styles.timer}>
                {formatTime(seconds)}
              </div>

              <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
                {t("LISTEN_BEFORE_UPLOAD") || "האזן להקלטה לפני העלאה"}
              </p>

              {/* Playback Controls */}
              <div className={styles.recordingControls}>
                <button
                  className={`${styles.recordButton} ${isPlaying ? styles.recording : ""}`}
                  onClick={togglePlayback}
                >
                  <span className={styles.icon}>{isPlaying ? "⏸️" : "▶️"}</span>
                  <span>{isPlaying ? (t("PAUSE_PLAYBACK") || "עצור") : (t("PLAY_RECORDING") || "נגן הקלטה")}</span>
                </button>
              </div>

              {/* Action Buttons */}
              <div className={`${styles.actionButtons} ${styles.active}`}>
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleUpload}>
                  <span className={styles.icon}>📤</span>
                  <span>{t("UPLOAD_RECORDING") || "העלה והמשך"}</span>
                </button>
                <button className={`${styles.btn} ${styles.btnCancel}`} onClick={handleCancelClick}>
                  <span className={styles.icon}>🗑️</span>
                  <span>{t("DELETE_RECORDING") || "מחק הקלטה"}</span>
                </button>
              </div>
            </div>
          )}

          {/* Processing Section */}
          {isProcessing && (
            <div className={`${styles.processing} ${styles.active}`}>
              <div className={styles.spinner}></div>
              <h3 className={styles.processingTitle}>{processingTitle}</h3>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progress}%` }}></div>
              </div>
              <p className={styles.processingText}>{processingText}</p>
              <p style={{ color: '#888', marginTop: '10px' }}>{t("PLEASE_WAIT") || "אנא המתן, זה עלול לקחת מספר דקות"}</p>
            </div>
          )}

          {/* Error Message */}
          {error && <div className={styles.error}>{error}</div>}
        </div>
      </div>

      {/* Inactive Patient Modal */}
      {showInactiveModal && (
        <Modal2 onClose={() => setShowInactiveModal(false)}>
          <DeleteConfirmation
            hideCloseButton
            text={t("ERROR_PATIENT_INACTIVE")}
            onDelete={async () => {
              setShowInactiveModal(false);
            }}
            onClose={() => setShowInactiveModal(false)}
          />
        </Modal2>
      )}

      {/* Start Recording Modal */}
      <div className={`${styles.modal} ${showStartModal ? styles.active : ""}`}>
        <div className={styles.modalContent}>
          <h3>🎙️ {t("START_RECORDING_MODAL_TITLE") || "התחלת הקלטת פגישה"}</h3>
          <p>{t("START_RECORDING_MODAL_TEXT") || "האם להתחיל הקלטת הפגישה? ההקלטה תתומלל ותסוכם אוטומטית בסיום."}</p>
          <div className={styles.modalButtons}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={confirmStart}>
              {t("START_RECORDING") || "התחל הקלטה"}
            </button>
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowStartModal(false)}>
              {t("CANCEL") || "ביטול"}
            </button>
          </div>
        </div>
      </div>

      {/* Stop Recording Modal */}
      <div className={`${styles.modal} ${showStopModal ? styles.active : ""}`}>
        <div className={styles.modalContent}>
          <h3>⏹️ {t("STOP_RECORDING_MODAL_TITLE") || "סיום ההקלטה"}</h3>
          <p>
            {t("STOP_RECORDING_MODAL_TEXT_PREVIEW") || "האם לסיים את ההקלטה? תוכל להאזין לה לפני ההעלאה."}
            <br />
            <strong>{t("RECORDING_DURATION") || "משך ההקלטה"}: {formatTime(seconds)}</strong>
          </p>
          <div className={styles.modalButtons}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={confirmStop}>
              {t("FINISH_RECORDING") || "סיים הקלטה"}
            </button>
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => {
              setShowStopModal(false);
              // Resume recording only if it was paused by the modal (not already paused by user)
              if (wasPausedByModalRef.current && mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
                mediaRecorderRef.current.resume();
                timerRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
              }
              wasPausedByModalRef.current = false;
            }}>
              {t("BACK_TO_RECORDING") || "חזור להקלטה"}
            </button>
          </div>
        </div>
      </div>

      {/* Cancel Recording Modal */}
      <div className={`${styles.modal} ${showCancelModal ? styles.active : ""}`}>
        <div className={styles.modalContent}>
          <h3>🗑️ {t("CANCEL_RECORDING_MODAL_TITLE") || "ביטול ההקלטה"}</h3>
          <p>{t("CANCEL_RECORDING_MODAL_TEXT") || "האם אתה בטוח שברצונך למחוק את ההקלטה? פעולה זו אינה ניתנת לביטול."}</p>
          <div className={styles.modalButtons}>
            <button className={`${styles.btn} ${styles.btnStop}`} onClick={confirmCancel}>
              {t("DELETE_RECORDING") || "מחק הקלטה"}
            </button>
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setShowCancelModal(false)}>
              {t("BACK") || "חזור"}
            </button>
          </div>
        </div>
      </div>

      {/* Leave Page During Recording Modal */}
      <div className={`${styles.modal} ${showLeaveModal ? styles.active : ""}`}>
        <div className={styles.modalContent}>
          <h3>⚠️ {t("LEAVE_RECORDING_TITLE") || "הקלטה פעילה"}</h3>
          <p>{t("LEAVE_RECORDING_TEXT") || "ההקלטה עדיין פעילה. אם תעזוב, תוכל לשחזר אותה בכניסה הבאה."}</p>
          <div className={styles.modalButtons}>
            <button className={`${styles.btn} ${styles.btnStop}`} onClick={confirmLeave}>
              {t("LEAVE_RECORDING_CONFIRM") || "עזוב"}
            </button>
            <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={cancelLeave}>
              {t("LEAVE_RECORDING_STAY") || "המשך הקלטה"}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
